import { MAX_TEXT, STATUS_OPTIONS, type CommentItem, type NewComment } from '@pathonline/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { sendJson } from '../api.ts';

interface Props {
  nodeId: string;
  label: string;
  canSetStatus: boolean;
  onPosted?: (c: CommentItem) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  /** Told on each change whether the box holds an unsent reply: text, or a status picked. */
  onDraft?: (has: boolean) => void;
}

/** Reply box: text, optional status change, Post (Ctrl/Cmd+Enter). */
export function Composer({ nodeId, label, canSetStatus, onPosted, onCancel, autoFocus, onDraft }: Props) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Gone (another thread opened, or no reply box any more): it holds no reply, and a post still on its way
  // when it went must not speak for the box that took its place.
  const gone = useRef(false);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
      onDraft?.(false);
    };
  }, []);
  // The text and status the box holds now, also for a post still on its way.
  const latest = useRef({ t: '', s: '' });
  const edit = (t: string, s: string) => {
    latest.current = { t, s };
    setText(t);
    setStatus(s);
    if (!gone.current) onDraft?.(t.trim() !== '' || s !== '');
  };

  const post = async () => {
    if (busy) return; // Ctrl+Enter while the first post is still on its way
    if (!text.trim() && !status) {
      setError('Write a reply first');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const body: NewComment = { text, ...(status ? { status } : {}) };
      const c = await sendJson<CommentItem>('POST', `/api/nodes/${encodeURIComponent(nodeId)}/comments`, body);
      // The comment is saved. Marking the thread read is extra: its failure must not look like a failed post.
      await sendJson('POST', '/api/comments/seen', { nodeIds: [nodeId] }).catch(() => undefined);
      // Only what was sent: a line added or a status picked while it was on its way stays.
      edit(latest.current.t === text ? '' : latest.current.t, latest.current.s === status ? '' : latest.current.s);
      await Promise.all([qc.invalidateQueries({ queryKey: ['views'] }), qc.invalidateQueries({ queryKey: ['unread'] })]);
      onPosted?.(c);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="composer">
      <textarea
        autoFocus={autoFocus}
        rows={3}
        maxLength={MAX_TEXT}
        placeholder={`Reply on “${label}”…`}
        value={text}
        onChange={(e) => edit(e.target.value, status)}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') void post();
          // Handled here: the page's own Escape (close the thread) leaves it alone.
          if (e.key === 'Escape' && onCancel) {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <div className="composer-row">
        {canSetStatus && (
          <select value={status} onChange={(e) => edit(text, e.target.value)} aria-label="Change the task's status">
            <option value="">No status change</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                Set {s}
              </option>
            ))}
          </select>
        )}
        {error && <span className="error small">{error}</span>}
        {onCancel && (
          <button type="button" className="link" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="button" className="btn small" disabled={busy} onClick={() => void post()}>
          {busy ? 'Posting…' : 'Post'}
        </button>
      </div>
    </div>
  );
}
